import { FC, useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { ButtonWallet } from '@/features/account/components';
import { getConnectorIcon } from '@/lib/utils';
import { useUnifiedConnectors } from '@/hooks';
import { Spinner } from '@/components/ui/spinner';
import useViewport from '@/hooks/use-viewport';
import { useAccountStore } from '@/store/use-account-store';
import { DesktopIcon } from '@radix-ui/react-icons';

import { Html5Qrcode } from 'html5-qrcode';
import { Scanner } from '@yudiel/react-qr-scanner';

interface ConnectModalProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  displayTrigger?: boolean;
}

export const ConnectModal: FC<ConnectModalProps> = ({
  isOpen: externalIsOpen,
  onOpenChange = () => {},
  displayTrigger = true,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const { connectors, isPending } = useUnifiedConnectors();
  const { isMobileConnected } = useAccountStore();
  const [qrCodeError, setQrCodeError] = useState<string>();
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  const viewport = useViewport();
  const isMobile = viewport == 'sm';

  const [isScanning, setIsScanning] = useState(false);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    if (isControlled) {
      setInternalIsOpen(externalIsOpen);
    }
  }, [externalIsOpen, isControlled]);

  const handleOpenChange = (newIsOpen: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };

  useEffect(() => {
    if (!isOpen) setIsScanning(false);
  }, [isOpen]);

  // const startScanning = async () => {
  //   if (isScanning) return;

  //   setIsScanning(true);

  //   const qrCodeRegionId = 'qr-reader';
  //   const html5QrCode = new Html5Qrcode(qrCodeRegionId);
  //   html5QrCodeRef.current = html5QrCode;

  //   try {
  //     await html5QrCode.start(
  //       { facingMode: 'environment' },
  //       { fps: 10, qrbox: 250 },
  //       (decodedText: string) => {
  //         console.error(decodedText);
  //         window.location.href = decodedText;
  //         html5QrCode.stop().then(() => {
  //           setIsScanning(false);
  //         });
  //       },
  //       (error: string) => {
  //         // Optional: handle scanning errors
  //         console.warn('Scanning Error:', error);
  //       },
  //     );
  //   } catch (error: any) {
  //     console.error(error);
  //     setQrCodeError(error);
  //   }
  // };

  // const stopScanning = () => {
  //   try {
  //     if (html5QrCodeRef.current) {
  //       html5QrCodeRef.current.stop().then(() => {
  //         html5QrCodeRef.current?.clear();
  //         setIsScanning(false);
  //       });
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="gap-0 border border-border bg-background p-0 sm:max-w-md sm:rounded-none">
          {isPending && (
            <div className="absolute z-10 flex h-full w-full items-center justify-center rounded-lg bg-black bg-opacity-50">
              <Spinner size="lg" />
            </div>
          )}
          <DialogHeader className={`px-5 ${isScanning ? 'py-6' : 'pt-6'}`}>
            <DialogTitle className="text-xl text-foreground">
              Connect Wallet
            </DialogTitle>
          </DialogHeader>

          {isScanning && (
            <div className="px-3 pb-3 text-center">
              Access {window.location.origin} on your desktop <br />
              to generate a one-time QR code.
            </div>
          )}

          {isScanning && (
            <Scanner
              onError={(err) => {
                setIsScanning(false);
                setQrCodeError(err as string);
              }}
              onScan={(codes) => {
                window.location.href = codes[0].rawValue;
                setIsScanning(false);
              }}
            />
          )}

          {/* <div
            id="qr-reader"
            className={`left-0 top-0 z-10 h-full w-full ${!isScanning ? 'hidden' : ''}`}
          /> */}
          {qrCodeError && (
            <div className="p-4 pt-0 text-center text-red">
              {String(qrCodeError)}
            </div>
          )}
          {!isScanning && (
            <div className="relative grid grid-cols-2 gap-4 p-5">
              {connectors.map(({ connector, handleConnect, name }) => (
                <ButtonWallet
                  key={name}
                  connector={connector}
                  icon={
                    connector.icon
                      ? connector.icon
                      : getConnectorIcon(connector)
                  }
                  label={name}
                  onClick={handleConnect}
                />
              ))}
              {/* {isMobile && ( */}
              <Button
                onClick={() => setIsScanning(true)}
                variant="outline"
                className={`relative h-auto px-3 py-6 hover:bg-vestgrey-800 ${isMobileConnected ? 'border-primary bg-vestgrey-800' : ''}`}
              >
                <div className="btn-wallet flex w-full flex-col items-center justify-start gap-3 font-mono text-lg font-normal">
                  <DesktopIcon className="h-12 w-12 rounded-md" />
                  DESKTOP WALLET
                </div>
                {isMobileConnected && (
                  <div className="absolute bottom-1.5 text-primary">
                    connected
                  </div>
                )}
              </Button>
              {/* )} */}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
